import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Autocomplete,
    Chip,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    suggestRequirementOwners,
    suggestRequirementTags,
} from '../../firebase/firestore/icr';
import {getUsersByIds} from '../../firebase/firestore/users';
import UserAvatar from '../../components/UserAvatar';
import TagBadge from '../../components/documentContent/TagBadge';
import {tagColor} from './tags/tagColor';
import {userDisplayName, toAvatarProfile} from '../collection/components/PersonCell';

function ownerKey(opt) {
    if (opt == null) return '';
    if (typeof opt === 'string') return opt;
    return opt.owner || opt.userId || '';
}

/** Prefer first + last name; fall back to displayName / raw owner. */
function ownerLabel(opt) {
    if (opt == null) return '';
    if (typeof opt === 'string') return opt;
    const fromParts = [opt.firstName, opt.lastName].filter(Boolean).join(' ').trim();
    if (fromParts) return fromParts;
    return userDisplayName(opt) || opt.displayName || opt.owner || '';
}

function toOwnerOption(raw) {
    if (raw == null) return null;
    if (typeof raw === 'string') {
        return {owner: raw, displayName: raw, hasAvatar: false};
    }
    const owner = raw.owner || raw.userId || raw.id || '';
    return {
        owner,
        userId: raw.userId || raw.id || undefined,
        firstName: raw.firstName || null,
        lastName: raw.lastName || null,
        displayName: raw.displayName || ownerLabel(raw) || owner,
        hasAvatar: raw.hasAvatar === true,
        profilePicture: raw.profilePicture || raw.photoURL || undefined,
    };
}

function OwnerOptionRow({option, size = 24}) {
    const label = ownerLabel(option);
    const userId = option?.userId || (option?.hasAvatar ? option?.owner : undefined);
    return (
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <UserAvatar
                user={toAvatarProfile(option) || {displayName: label}}
                userId={userId}
                sx={{width: size, height: size, fontSize: size <= 20 ? '0.62rem' : '0.7rem'}}
            />
            <Typography variant="body2" noWrap title={label}>{label}</Typography>
        </Stack>
    );
}

/**
 * Shared Owner + Tags multiselects used on Overview and in the ICR/EEI filter panel.
 * Owner values in parent state remain raw owner strings (filter keys); UI shows
 * avatar + first/last name when a user profile is available.
 */
export default function RequirementsOwnerTagsFilters({
    owners,
    tags,
    onOwnersChange,
    onTagsChange,
    ownerLabel: fieldLabel = 'Owner',
    /** When set (`icr` / `eei`), owner suggestions are scoped to that entity type. */
    entityType,
    direction = {xs: 'column', md: 'row'},
}) {
    const [tagOptions, setTagOptions] = useState([]);
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [ownersLoading, setOwnersLoading] = useState(false);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [selectedProfiles, setSelectedProfiles] = useState({});
    const ownerSuggestSeq = useRef(0);
    const tagSuggestSeq = useRef(0);

    const handleTagInput = useCallback(async (value) => {
        const seq = ++tagSuggestSeq.current;
        setTagsLoading(true);
        try {
            const names = await suggestRequirementTags(value || '', 20);
            if (seq !== tagSuggestSeq.current) return;
            setTagOptions(Array.isArray(names) ? names : []);
        } catch {
            if (seq !== tagSuggestSeq.current) return;
            setTagOptions([]);
        } finally {
            if (seq === tagSuggestSeq.current) setTagsLoading(false);
        }
    }, []);

    const handleOwnerInput = useCallback(async (value) => {
        const seq = ++ownerSuggestSeq.current;
        setOwnersLoading(true);
        try {
            const next = await suggestRequirementOwners(value || '', 20, entityType);
            if (seq !== ownerSuggestSeq.current) return;
            const normalized = (Array.isArray(next) ? next : [])
                .map(toOwnerOption)
                .filter((o) => o && o.owner);
            setOwnerOptions(normalized);
        } catch {
            if (seq !== ownerSuggestSeq.current) return;
            setOwnerOptions([]);
        } finally {
            if (seq === ownerSuggestSeq.current) setOwnersLoading(false);
        }
    }, [entityType]);

    // Drop cached suggestions when switching ICR/EEI so the other tab's owners do not linger.
    useEffect(() => {
        setOwnerOptions([]);
    }, [entityType]);

    // Resolve profiles for selected owner keys (e.g. CM default uid before dropdown opens).
    useEffect(() => {
        const keys = (owners || []).filter(Boolean);
        if (!keys.length) return undefined;
        let cancelled = false;
        Promise.resolve(getUsersByIds(keys))
            .then((users) => {
                if (cancelled || !Array.isArray(users)) return;
                const next = {};
                for (const u of users || []) {
                    const id = u?.id || u?.userId;
                    if (!id) continue;
                    next[id] = toOwnerOption({
                        owner: id,
                        userId: id,
                        firstName: u.firstName,
                        lastName: u.lastName,
                        displayName: userDisplayName(u) || id,
                        hasAvatar: u.hasAvatar === true,
                        profilePicture: u.profilePicture,
                    });
                }
                setSelectedProfiles((prev) => ({...prev, ...next}));
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [owners]);

    const optionByOwner = useMemo(() => {
        const map = {...selectedProfiles};
        for (const opt of ownerOptions) {
            if (opt?.owner) map[opt.owner] = opt;
        }
        return map;
    }, [ownerOptions, selectedProfiles]);

    const valueObjects = useMemo(
        () => (owners || []).map((key) => optionByOwner[key] || toOwnerOption(key)),
        [owners, optionByOwner],
    );

    return (
        <Stack direction={direction} spacing={1.5}>
            <Autocomplete
                multiple
                size="small"
                fullWidth
                options={ownerOptions}
                value={valueObjects}
                filterSelectedOptions
                getOptionLabel={ownerLabel}
                isOptionEqualToValue={(a, b) => ownerKey(a) === ownerKey(b)}
                onChange={(_, value) => {
                    onOwnersChange((value || []).map(ownerKey).filter(Boolean));
                }}
                loading={ownersLoading}
                loadingText="Loading…"
                noOptionsText={ownersLoading ? 'Loading…' : 'No options'}
                onOpen={() => { handleOwnerInput(''); }}
                onInputChange={(_, value, reason) => {
                    if (reason !== 'reset') handleOwnerInput(value);
                }}
                renderOption={(props, option) => {
                    const {key, ...optionProps} = props;
                    return (
                        <li key={key || ownerKey(option)} {...optionProps}>
                            <OwnerOptionRow option={option} size={24}/>
                        </li>
                    );
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const {key, ...tagProps} = getTagProps({index});
                        const label = ownerLabel(option);
                        const userId = option?.userId || (option?.hasAvatar ? option?.owner : undefined);
                        return (
                            <Chip
                                {...tagProps}
                                key={key || ownerKey(option)}
                                size="small"
                                avatar={(
                                    <UserAvatar
                                        user={toAvatarProfile(option) || {displayName: label}}
                                        userId={userId}
                                        sx={{width: 18, height: 18, fontSize: '0.62rem'}}
                                    />
                                )}
                                label={label}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={fieldLabel}
                        placeholder="Add owner…"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {ownersLoading ? <CircularProgress color="inherit" size={16}/> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            <Autocomplete
                multiple
                size="small"
                fullWidth
                options={tagOptions}
                value={tags}
                filterSelectedOptions
                onChange={(_, value) => onTagsChange(value)}
                loading={tagsLoading}
                loadingText="Loading…"
                noOptionsText={tagsLoading ? 'Loading…' : 'No options'}
                onOpen={() => { handleTagInput(''); }}
                onInputChange={(_, value, reason) => {
                    if (reason !== 'reset') handleTagInput(value);
                }}
                renderOption={(props, option) => {
                    const {key, ...optionProps} = props;
                    return (
                        <li key={key} {...optionProps}>
                            <TagBadge tag={option}/>
                        </li>
                    );
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const colors = tagColor(option);
                        const {key, ...tagProps} = getTagProps({index});
                        return (
                            <Chip
                                {...tagProps}
                                key={key}
                                size="small"
                                label={option}
                                sx={{
                                    fontWeight: 700,
                                    bgcolor: colors.bg,
                                    color: colors.fg,
                                    border: `1px solid ${colors.border}`,
                                    '& .MuiChip-deleteIcon': {color: colors.fg},
                                }}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Tags"
                        placeholder="Add tag…"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {tagsLoading ? <CircularProgress color="inherit" size={16}/> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </Stack>
    );
}
